import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Input } from 'antd';
import button from '@components/ui-kit/buttons/submitButton.module.css';
import styles from './edit-scene-modal.module.css';
import {
  useUpdateUserSceneMutation,
  useCreateUserSceneMutation,
  useCreateUserSceneAssetsMutation,
  GetUserScenesDocument,
  useSceneTemplatesQuery,
  Brand,
  FogType,
  FogPreset,
} from '@src/components/generated/graphql';
import { DEFAULT_SCENE_TEMPLATE } from 'pages/user/studio/scene/[id]';
import { useBranding } from '@src/theme/branding/useBranding';
import { DEFAULT_LIGHT_TEMPLATE } from './editor/Editor';
import { DEFAULT_AMBIENT_LIGHT_IN_NEW_SCENE } from 'util/constants';

enum SCENES_TYPE {
  TEAM_SCENES = 0,
  MY_SCENES = 1,
}

export default function EditSceneModal({
  isModalEditSceneOpen,
  setIsModalEditSceneOpen,
  userScene,
  userId,
  selectedScenesType,
  teamId,
}) {
  const { isZepeto } = useBranding();
  const [updateUserScene] = useUpdateUserSceneMutation();
  const [createUserScene] = useCreateUserSceneMutation();
  const [createAsset] = useCreateUserSceneAssetsMutation();
  const [isCreatingScene, setIsCreatingScene] = useState(false);

  const {
    data: sceneTemplates,
    loading: l,
    error: e,
  } = useSceneTemplatesQuery();

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      sceneName: userScene?.name || '',
      sceneDescription: userScene?.description || '',
    });
  }, [form, userScene]);

  if (l || e) {
    return <></>;
  }

  const defaultSceneTemplate =
    sceneTemplates?.sceneTemplates[DEFAULT_SCENE_TEMPLATE];
  const sceneId = userScene?.id;

  const handleEditCancel = () => {
    setIsModalEditSceneOpen(false);
  };

  const onFinish = async (values: any) => {
    setIsCreatingScene(true);
    if (sceneId) {
      await updateUserScene({
        variables: {
          where: {
            id: sceneId,
          },
          data: {
            name: {
              set: values.sceneName,
            },
            description: {
              set: values.sceneDescription,
            },
          },
        },
        refetchQueries: ['UserScenes'],
      });
    } else {
      const createScene = await createUserScene({
        variables: {
          data: {
            name: values.sceneName,
            description: values.sceneDescription,
            thumbnail: defaultSceneTemplate?.thumbnail ?? '',
            userId: userId,
            branding: isZepeto ? Brand.Zepeto : Brand.Studio,
            lightTemplate: DEFAULT_LIGHT_TEMPLATE,
            teamId:
              selectedScenesType === SCENES_TYPE.TEAM_SCENES && teamId
                ? teamId
                : null,
            graphicsSettings: {
              create: {
                preset: 'medium',
                postProcessingSettings: {
                  create: {
                    antialiasing: 'off',
                    bloom: 'off',
                    ssr: 'off',
                    ssao: 'off',
                    bloomExposure: 0.75,
                    bloomRadius: 0.5,
                    bloomStrength: 0.75,
                    bloomThreshold: 0.3,
                    ssrOpacity: 0.1,
                    ssrWidth: 960,
                    ssrHeight: 540,
                    ssrMaxDistance: 8,
                  },
                },
                cloudConfig: {
                  create: {
                    enabled: false,
                    count: 16,
                    speed: 1.4,
                    scaleX: 408,
                    scaleY: 119,
                    scaleZ: 408,
                    opacity: 0.25,
                    colorHex: '#798aa0',
                    windDirectionX: 1,
                    windDirectionY: 0,
                    windDirectionZ: 0.2,
                    spawnAreaMinX: -600,
                    spawnAreaMinY: 360,
                    spawnAreaMinZ: -600,
                    spawnAreaMaxX: 600,
                    spawnAreaMaxY: 480,
                    spawnAreaMaxZ: 600,
                    threshold: 0.25,
                    range: 0.1,
                    steps: 100,
                    variationOpacity: 0.15,
                    variationScale: 0.4,
                    variationThreshold: 0,
                    variationColor: 0.5,
                  },
                },
                fogConfig: {
                  create: {
                    enabled: false,
                    type: FogType.Exponential,
                    colorHex: '#cccccc',
                    near: 1,
                    far: 1000,
                    density: 0.00025,
                    autoUpdate: false,
                    preset: FogPreset.Custom,
                  },
                },
              },
            },
          },
        },
        refetchQueries: [GetUserScenesDocument],
      });

      const newSceneId = createScene.data?.createOneUserScene.id;

      if (DEFAULT_SCENE_TEMPLATE >= 0 && defaultSceneTemplate?.baseModelURL) {
        const assetNames = [
          'AssetPack',
          'HighPack',
          'SignPack',
          'TeleportFloor',
        ];

        const assetsURL: string[] = assetNames.map(
          (assetName) => `${defaultSceneTemplate.baseModelURL}${assetName}.gltf`
        );

        assetsURL.forEach(async (url, index) => {
          await createAsset({
            variables: {
              data: {
                name: assetNames[index],
                description: `Scene ${newSceneId} asset ${assetNames[index]}`,
                type: assetNames[index].toUpperCase(),
                url,
                previewImage: url,
                userId,
                position: {
                  set: [0, 0, 0],
                },
                rotation: {
                  set: [0, 0, 0],
                },
                scale: {
                  set: [1, 1, 1],
                },
                userScene: {
                  connect: {
                    id: newSceneId,
                  },
                },
              },
            },
            refetchQueries: ['FindManyUserSceneAssets'],
          });
        });
      }

      await createAsset({
        variables: {
          data: {
            ...DEFAULT_AMBIENT_LIGHT_IN_NEW_SCENE,
            userId,
            userScene: {
              connect: {
                id: newSceneId,
              },
            },
          },
        },
        refetchQueries: ['FindManyUserSceneAssets'],
      });
    }
    setIsCreatingScene(false);
    setIsModalEditSceneOpen(false);
  };

  return (
    <Modal
      title={sceneId ? 'Edit scene info' : 'Create Scene'}
      centered
      width={480}
      open={isModalEditSceneOpen}
      footer={null}
      zIndex={1002}
      onCancel={handleEditCancel}
    >
      <Form
        form={form}
        name='basic'
        onFinish={onFinish}
        className={styles.form}
        layout='vertical'
        autoComplete='off'
      >
        <Form.Item
          label={<span className={styles.label}>Scene name</span>}
          name='sceneName'
          rules={[{ required: true, message: 'Please input your scene name!' }]}
        >
          <Input
            className={styles.input}
            maxLength={45}
            data-cy='scene-name-input'
          />
        </Form.Item>

        <Form.Item
          label={<span className={styles.label}>Scene Description</span>}
          name='sceneDescription'
          rules={[{ required: false, message: 'Scene description' }]}
        >
          <Input.TextArea
            maxLength={70}
            className={styles.inputTextArea}
            data-cy='scene-description-input'
          />
        </Form.Item>

        <Form.Item className='edit-scene-modal-button'>
          <Button
            className={button.primaryButton}
            type='primary'
            htmlType='submit'
            loading={isCreatingScene}
            data-cy='create-scene-submit-button'
          >
            <span style={{ color: '#fff' }}>
              {sceneId ? 'Save changes' : 'Create scene'}
            </span>
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
